const PUBLIC_GALLERY_MOSAIC_PATCH = Symbol.for('eInvitation.publicGalleryMosaicLayout.v2')
const initializedImages = new WeakSet()
const initializedGrids = new WeakSet()
const layoutFrames = new WeakMap()

function classifyGalleryShape(image) {
  const width = Number(image.naturalWidth) || 0
  const height = Number(image.naturalHeight) || 0
  if (!width || !height) return 'square'

  const ratio = width / height
  if (ratio >= 1.35) return 'landscape'
  if (ratio <= 0.8) return 'portrait'
  return 'square'
}

function readNumber(value, fallback = 0) {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function calculateGridSpan(grid, item) {
  const styles = window.getComputedStyle(grid)
  const rowHeight = Math.max(1, readNumber(styles.gridAutoRows, 4))
  const rowGap = Math.max(0, readNumber(styles.rowGap, 0))
  const columnGap = Math.max(0, readNumber(styles.columnGap, 0))
  const gridWidth = grid.clientWidth
  const columnWidth = Math.max(1, (gridWidth - columnGap) / 2)
  const shape = item.dataset.galleryShape || 'square'

  let targetHeight = columnWidth
  if (shape === 'landscape') targetHeight = gridWidth * 9 / 16
  if (shape === 'portrait') targetHeight = (columnWidth * 2) + rowGap

  return Math.max(1, Math.ceil((targetHeight + rowGap) / (rowHeight + rowGap)))
}

function layoutGrid(grid) {
  layoutFrames.delete(grid)
  if (!(grid instanceof HTMLElement) || !grid.isConnected || grid.clientWidth <= 0) return

  grid.querySelectorAll('.epa-public-photo-button').forEach((item) => {
    item.style.gridRowEnd = `span ${calculateGridSpan(grid, item)}`
  })
}

function scheduleGridLayout(grid) {
  if (!(grid instanceof HTMLElement) || layoutFrames.has(grid)) return
  const frame = requestAnimationFrame(() => layoutGrid(grid))
  layoutFrames.set(grid, frame)
}

function initializeGrid(grid) {
  if (!(grid instanceof HTMLElement)) return
  grid.dataset.galleryMode = 'mosaic'

  if (initializedGrids.has(grid)) {
    scheduleGridLayout(grid)
    return
  }

  initializedGrids.add(grid)
  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(() => scheduleGridLayout(grid))
    : null
  resizeObserver?.observe(grid)
  scheduleGridLayout(grid)
}

function applyShape(item, image) {
  const shape = classifyGalleryShape(image)
  item.dataset.galleryShape = shape
  image.dataset.galleryShape = shape
  item.setAttribute('aria-label', `${shape} public event photo`)
  initializeGrid(item.closest('.epa-public-photo-grid'))
}

function initializeImage(image) {
  if (!(image instanceof HTMLImageElement) || initializedImages.has(image)) return
  initializedImages.add(image)

  const item = image.closest('.epa-public-photo-button')
  if (!(item instanceof HTMLElement)) return

  item.tabIndex = -1
  item.setAttribute('aria-disabled', 'true')
  item.style.cursor = 'default'
  item.dataset.galleryShape ||= 'square'

  const measure = () => applyShape(item, image)
  image.addEventListener('load', measure, { passive: true })
  if (image.complete && image.naturalWidth > 0) measure()
  else initializeGrid(item.closest('.epa-public-photo-grid'))
}

function disablePhotoInteraction(event) {
  const item = event.target?.closest?.('.epa-native-public-gallery .epa-public-photo-button')
  if (!item) return
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation?.()
}

function scan(root = document) {
  root.querySelectorAll?.('.epa-native-public-gallery .epa-public-photo-grid')
    .forEach(initializeGrid)

  root.querySelectorAll?.('.epa-native-public-gallery .epa-public-photo-image')
    .forEach(initializeImage)
}

export function installPublicGalleryMosaicLayout() {
  if (typeof document === 'undefined' || globalThis[PUBLIC_GALLERY_MOSAIC_PATCH]) return

  const start = () => {
    scan(document)

    document.addEventListener('click', disablePhotoInteraction, true)
    document.addEventListener('dblclick', disablePhotoInteraction, true)
    document.addEventListener('contextmenu', disablePhotoInteraction, true)

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue
          if (node.matches?.('.epa-public-photo-grid')) initializeGrid(node)
          if (node.matches?.('.epa-public-photo-image')) initializeImage(node)
          scan(node)
        }
      }
    })

    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  globalThis[PUBLIC_GALLERY_MOSAIC_PATCH] = true
}
